import cookie from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import type { JwtPayload } from 'jwt-decode';

import type { IdentityToken, IdentityTokenPayload, RoleScope } from 'types';
import { STORAGE } from 'utils';

const {
  COOKIE: {
    IDENTITY: {
      ACCESS_TOKEN: IDENTITY_ACCESS_TOKEN,
      REFRESH_TOKEN: IDENTITY_REFRESH_TOKEN,
      TOKEN_TYPE: IDENTITY_TOKEN_TYPE,
    },
  },
} = STORAGE;

const setIdentityToken = async ({
  accessToken,
  refreshToken,
  tokenType,
  expireAt,
}: IdentityToken) => {
  const options: Parameters<typeof cookie.set>[2] = {
    expires: new Date(expireAt),
  };

  await Promise.all([
    removeIdentityToken(),
    cookie.set(IDENTITY_ACCESS_TOKEN, accessToken, options),
    cookie.set(IDENTITY_REFRESH_TOKEN, refreshToken, options),
    cookie.set(IDENTITY_TOKEN_TYPE, tokenType, options),
  ]);
};

const removeIdentityToken = async () => {
  const options: Parameters<typeof cookie.remove>[1] = {};

  await Promise.all([
    cookie.remove(IDENTITY_ACCESS_TOKEN, options),
    cookie.remove(IDENTITY_REFRESH_TOKEN, options),
    cookie.remove(IDENTITY_TOKEN_TYPE, options),
  ]);
};

const getIdentityToken = async (): Promise<Partial<IdentityToken>> => {
  const [accessToken, refreshToken, tokenType] = await Promise.all([
    cookie.get(IDENTITY_ACCESS_TOKEN),
    cookie.get(IDENTITY_REFRESH_TOKEN),
    cookie.get(IDENTITY_TOKEN_TYPE),
  ]);

  return {
    accessToken,
    refreshToken,
    tokenType: tokenType as IdentityToken['tokenType'],
  };
};

const decodeIdentityToken = async <
  T extends JwtPayload = JwtPayload & IdentityTokenPayload,
>(
  options?: Parameters<typeof jwtDecode<T>>[1],
): Promise<
  Partial<Record<'decodedAccessToken' | 'decodedRefreshToken', T>>
> => {
  const { accessToken, refreshToken } = await getIdentityToken();

  const parseToken = (token?: Parameters<typeof jwtDecode<T>>[0]) => {
    if (!token) return;
    return jwtDecode<T>(token, options);
  };

  const [decodedAccessToken, decodedRefreshToken] = await Promise.all([
    parseToken(accessToken),
    parseToken(refreshToken),
  ]);

  return {
    decodedAccessToken,
    decodedRefreshToken,
  };
};

const verifyTokenPermission = async (
  permissions: RoleScope | RoleScope[],
  allowOwnersBypass = true,
  strategy: 'some' | 'every' = 'some',
) => {
  const { decodedAccessToken } = await decodeIdentityToken();

  const { role: userRoles = [], scope: userScopes = [] } =
    decodedAccessToken ?? {};
  const permissionsList = Array.isArray(permissions)
    ? permissions
    : [permissions];
  const userRolesList = Array.isArray(userRoles) ? userRoles : [userRoles];

  const hasOwnerRole = userRolesList.includes('Owner');
  const hasRequiredPermissions = permissionsList[strategy](permission =>
    userScopes.includes(permission),
  );

  return allowOwnersBypass
    ? hasOwnerRole || hasRequiredPermissions
    : hasRequiredPermissions;
};

export {
  decodeIdentityToken,
  getIdentityToken,
  removeIdentityToken,
  setIdentityToken,
  verifyTokenPermission,
};
